import React, { useState, useRef, useMemo, useEffect, useCallback } from "react";
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Tooltip,
  useMediaQuery,
  useTheme,
  Typography,
  Paper,
  List,
  ListItemButton,
  ClickAwayListener,
  Chip,
  Alert,
} from "@mui/material";
import { InsertEmoticon, Mic, Send, Close, InfoOutlined, AutoAwesome } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import ChannelSelector from "./ChannelSelector";
import ComposeAttachMenu from "./ComposeAttachMenu";
import { useComposeSelectionFormat } from "../../hooks/useComposeSelectionFormat";
import { getChannelById, getChannelComposerSurfaceSx } from "../../utils/utils";
import { useChannels } from "../../hooks/useChannels";
import { isActiveChannel } from "../../constants/channelCatalog";
import { composerHasSendPayload } from "./composeAttachmentHelpers";
import {
  COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
  COMPOSER_MOBILE_ROW_HEIGHT_PX,
  COMPOSER_ROW_HEIGHT_PX,
  composerMobileInputRowSx,
  composerPrimaryActionButtonSx,
} from "./composerConstants";
import { COMPOSER_MOBILE_TEXT_FIELD_SX, COMPOSER_TEXT_FIELD_SX } from "./composerTextFieldStyles";
import RecordingIndicator from "./RecordingIndicator";
import AudioPreview from "./AudioPreview";
import ComposerMobileToolbar from "./ComposerMobileToolbar";
import {
  isWhatsappTemplateUsable,
  whatsappTemplateStatusKind,
} from "../../utils/whatsappTemplateLifecycle";

const SUGGESTIONS_MAX_HEIGHT = 280;
const SUGGESTION_PREVIEW_LENGTH = 120;

/** Normaliza string para busca ignorando acentos (lowercase, sem diacríticos). */
function normalizeForSearch(s) {
  if (s == null || typeof s !== "string") return "";
  return s
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

const MessageWhatsappChannel = ({
  message,
  setMessage,
  channelType,
  selectedChannel,
  setSelectedChannel,
  formatOptions,
  startRecording,
  openFilePicker,
  handleEmojiPickerOpen,
  handleSendMessage,
  file,
  files = [],
  audioURL,
  onGenerateSuggestion,
  isGeneratingSuggestion = false,
  hasHistory = false,
  suggestions = { templates: [], quickReplies: [] },
  isLoadingSuggestions = false,
  suggestionsError = null,
  requiresTemplate = false,
  selectedTemplate = null,
  onTemplateSelect,
  externalPickerOpen = false,
  sendBlockedMissingPhone = false,
  sendComposeBlocked = false,
  onSendCurrentLocation,
  locationSending = false,
  locationSendDisabled = false,
  locationSendDisabledHelp = null,
  onShareContact,
  contactSendDisabled = false,
  contactSendDisabledHelp = null,
  onStartVideo,
  videoSending = false,
  videoSendDisabled = false,
  videoSendDisabledHelp = null,
  videoMenuLabel = null,
  onJoinVideo,
  joinVideoSending = false,
  showJoinVideo = false,
  onStartGoogleMeet,
  googleMeetSending = false,
  googleMeetDisabled = false,
  googleMeetDisabledHelp = null,
  onOpenProductPicker,
  showPoll = false,
  onStartPoll,
  pollDisabled = false,
  pollDisabledHelp = null,
  isPersonalWhatsappThread = false,
  isRecording = false,
  recordingDuration = 0,
  stopRecording = () => {},
  discardRecording = () => {},
  onRemoveAudio,
  clientConversation = null,
  conversationMessages = null,
}) => {
  const { t } = useTranslation("chat");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [suggestionsDismissed, setSuggestionsDismissed] = useState(false);
  const [windowAlertDismissed, setWindowAlertDismissed] = useState(false);
  const [manualTemplatesOpen, setManualTemplatesOpen] = useState(false);
  const inputRef = useRef(null);
  const anchorRef = useRef(null);
  const highlightedItemRef = useRef(null);
  const isMobile = useMediaQuery("(max-width: 768px)");
  const theme = useTheme();
  const inputBorderRadius = 16; // consistente com o campo do chat
  const composerRowHeight = isMobile ? COMPOSER_MOBILE_ROW_HEIGHT_PX : COMPOSER_ROW_HEIGHT_PX;
  const { data: channelsData } = useChannels();
  const channels = channelsData?.channels;
  const currentChannel = getChannelById(channels, selectedChannel);
  const channelName = currentChannel?.nome || currentChannel?.name || null;
  const composerSurface = getChannelComposerSurfaceSx(theme, {
    channelType,
    channelName,
    isInternalNote: false,
    isPersonalWhatsapp: isPersonalWhatsappThread,
  });
  /** Com mais de um WhatsApp no workspace, o tipo não diz por qual canal o envio sai. */
  const hasSiblingChannelsOfSameType = useMemo(() => {
    if (!currentChannel) return false;
    return (
      (channels || []).filter(
        (ch) =>
          isActiveChannel(ch) &&
          (ch.channelType
            ? ch.channelType === currentChannel.channelType
            : Number(ch.tipo) === Number(currentChannel.tipo))
      ).length > 1
    );
  }, [channels, currentChannel]);
  const showAiButton = !!onGenerateSuggestion && channelType !== "Xpro";
  const aiDisabled = isGeneratingSuggestion || !hasHistory;
  const templateAvailability = useMemo(() => {
    const rows = Array.isArray(suggestions?.templates) ? suggestions.templates : [];
    const usable = rows.filter(isWhatsappTemplateUsable);
    const pendingCount = rows.filter((tpl) => whatsappTemplateStatusKind(tpl) === "pending").length;
    const blockedCount = rows.filter(
      (tpl) => !isWhatsappTemplateUsable(tpl) && whatsappTemplateStatusKind(tpl) !== "pending"
    ).length;
    return { rows, usable, pendingCount, blockedCount };
  }, [suggestions?.templates]);
  const hasTemplatesAvailable = templateAvailability.usable.length > 0;
  const canSend = composerHasSendPayload(message, file, files, audioURL);

  const noUsableReason =
    !hasTemplatesAvailable && templateAvailability.pendingCount
      ? "pending"
      : !hasTemplatesAvailable && templateAvailability.blockedCount
        ? "blocked"
        : "none";

  const composerPlaceholder = useMemo(() => {
    if (requiresTemplate) {
      if (hasTemplatesAvailable) return t("input.placeholderTemplateSearch", "Buscar template…");
      if (noUsableReason === "pending") {
        return t("input.placeholderTemplatesPending", "Templates em aprovação");
      }
      if (noUsableReason === "blocked") {
        return t("input.placeholderTemplatesBlocked", "Nenhum template aprovado");
      }
      return t("input.placeholderNoTemplates", "Sem templates de reengajamento");
    }
    if (channelType === "Xpro") return t("input.placeholderInternal");
    if (isPersonalWhatsappThread) return t("input.placeholderPersonalWhatsapp");
    return t("input.placeholderChannel", {
      channel: hasSiblingChannelsOfSameType && channelName ? channelName : channelType,
    });
  }, [
    requiresTemplate,
    hasTemplatesAvailable,
    noUsableReason,
    channelType,
    channelName,
    hasSiblingChannelsOfSameType,
    isPersonalWhatsappThread,
    t,
  ]);

  const composerPlaceholderTitle = useMemo(() => {
    if (requiresTemplate) {
      if (hasTemplatesAvailable) {
        return t(
          "input.placeholderTemplateSearchHint",
          "Use um template de reengajamento (digite para buscar)"
        );
      }
      if (noUsableReason === "pending") {
        return t(
          "input.placeholderTemplatesPendingHint",
          "Há templates neste canal ainda em aprovação na Meta. Quando forem aprovados, aparecem aqui para envio."
        );
      }
      if (noUsableReason === "blocked") {
        return t(
          "input.placeholderTemplatesBlockedHint",
          "Há templates neste canal, mas nenhum está aprovado. Abra o canal em Configurações, veja o motivo e crie um novo."
        );
      }
      return t(
        "input.placeholderNoTemplatesHint",
        "Este canal ainda não tem template aprovado. Crie um em Configurações > Canais e aguarde a Meta aprovar."
      );
    }
    return composerPlaceholder;
  }, [requiresTemplate, hasTemplatesAvailable, noUsableReason, composerPlaceholder, t]);

  const { composeInputHandlers, selectionToolbar } = useComposeSelectionFormat({
    inputRef,
    setMessage,
    channelType,
    formatOptions,
  });

  const handleOpenProductPickerFromMenu = useCallback(() => {
    onOpenProductPicker?.();
    queueMicrotask(() => inputRef.current?.focus());
  }, [onOpenProductPicker]);

  const combinedSuggestions = useMemo(() => {
    const list = [];
    templateAvailability.usable.forEach((tpl) => {
      const fullText = (tpl.fullText || tpl.preview || tpl.name || "").trim();
      const displayText = (tpl.preview || tpl.name || fullText || "").trim();
      if (displayText) {
        list.push({
          type: "template",
          id: tpl.id,
          name: tpl.name,
          displayText,
          fullText,
          raw: tpl,
        });
      }
    });
    (suggestions?.quickReplies || []).forEach((qr) => {
      const displayText = (qr.text || "").trim();
      if (displayText) {
        list.push({
          type: "quickReply",
          id: qr.id,
          name: qr.text,
          displayText,
          fullText: displayText,
          raw: qr,
        });
      }
    });
    return list;
  }, [templateAvailability.usable, suggestions?.quickReplies]);

  const searchTrim = (message || "").trim();
  const filteredSuggestions = useMemo(() => {
    if (searchTrim.length === 0) return [];
    const normSearch = normalizeForSearch(searchTrim);
    return combinedSuggestions.filter(
      (item) =>
        (item.displayText && normalizeForSearch(item.displayText).includes(normSearch)) ||
        (item.name && normalizeForSearch(String(item.name)).includes(normSearch))
    );
  }, [combinedSuggestions, searchTrim]);

  const suggestionsList = useMemo(() => {
    // Fora da janela de 24h/primeiro contato, o operador precisa escolher um template.
    // Nesse caso, quando o campo estiver vazio, mostramos a lista completa de templates
    // (sem depender de match do texto), e filtramos conforme o usuário digita.
    if (requiresTemplate && hasTemplatesAvailable) {
      if (searchTrim.length === 0) {
        return (combinedSuggestions || []).filter((x) => x.type === "template");
      }
      // Se digitou algo mas não encontrou match, ainda assim mantém a lista de templates
      // para permitir seleção (a digitação pode ser uma saudação, etc.).
      return filteredSuggestions.length > 0 ? filteredSuggestions : (combinedSuggestions || []).filter((x) => x.type === "template");
    }
    return filteredSuggestions;
  }, [
    combinedSuggestions,
    filteredSuggestions,
    hasTemplatesAvailable,
    requiresTemplate,
    searchTrim.length,
  ]);

  const templatesOnlyList = useMemo(
    () => (combinedSuggestions || []).filter((x) => x.type === "template"),
    [combinedSuggestions]
  );

  const shouldShowTemplatesWhileEmpty =
    requiresTemplate && hasTemplatesAvailable && manualTemplatesOpen && searchTrim.length === 0;

  const suggestionsOpen =
    !externalPickerOpen &&
    !suggestionsDismissed &&
    (searchTrim.length >= 1 ? suggestionsList.length > 0 : shouldShowTemplatesWhileEmpty);

  const openTemplatesManually = () => {
    if (!requiresTemplate || !hasTemplatesAvailable) return;
    if (externalPickerOpen) return;
    setManualTemplatesOpen(true);
    setSuggestionsDismissed(false);
    setHighlightedIndex(0);
    queueMicrotask(() => inputRef.current?.focus());
  };

  // Se abriu picker externo (@/#), fecha templates para não conflitar
  useEffect(() => {
    if (!externalPickerOpen) return;
    setManualTemplatesOpen(false);
    setSuggestionsDismissed(true);
  }, [externalPickerOpen]);

  useEffect(() => {
    if (!suggestionsOpen) setHighlightedIndex(0);
    else setHighlightedIndex((i) => Math.min(i, suggestionsList.length - 1));
  }, [suggestionsOpen, suggestionsList.length]);

  // Rolagem para manter o item destacado visível ao navegar com setas
  useEffect(() => {
    if (!suggestionsOpen || highlightedIndex < 0) return;
    highlightedItemRef.current?.scrollIntoView({
      block: "nearest",
      behavior: "smooth",
      inline: "nearest",
    });
  }, [suggestionsOpen, highlightedIndex]);

  const closeSuggestions = () => {
    setHighlightedIndex(0);
    setSuggestionsDismissed(true);
    setManualTemplatesOpen(false);
  };

  const selectSuggestion = (item) => {
    const textToUse =
      item.type === "template"
        ? (item.fullText || item.displayText)
        : item.displayText;

    setMessage(textToUse);

    if (item.type === "template" && typeof onTemplateSelect === "function") {
      onTemplateSelect({
        id: item.id,
        name: item.name,
        preview: item.displayText,
        raw: item.raw,
      });
    } else if (typeof onTemplateSelect === "function") {
      onTemplateSelect(null);
    }

    setSuggestionsDismissed(true);
    setHighlightedIndex(0);
    if (inputRef.current) inputRef.current.focus();
  };

  useEffect(() => {
    if (searchTrim.length === 0) {
      // não abre automaticamente; apenas reabilita caso o usuário digite novamente
      setSuggestionsDismissed(false);
    }
  }, [searchTrim.length]);

  const handleInputKeyDown = (e) => {
    if (suggestionsOpen) {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setHighlightedIndex((i) => (i < filteredSuggestions.length - 1 ? i + 1 : 0));
        return;
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        setHighlightedIndex((i) => (i > 0 ? i - 1 : filteredSuggestions.length - 1));
        return;
      }
      if (e.key === "Enter" && suggestionsList[highlightedIndex]) {
        e.preventDefault();
        selectSuggestion(suggestionsList[highlightedIndex]);
        return;
      }
      if (e.key === "Escape") {
        e.preventDefault();
        closeSuggestions();
        return;
      }
    }
    // Atalho: Ctrl+Espaço abre templates quando obrigatório
    if (
      (e.ctrlKey || e.metaKey) &&
      e.code === "Space" &&
      requiresTemplate &&
      hasTemplatesAvailable &&
      !externalPickerOpen
    ) {
      e.preventDefault();
      openTemplatesManually();
      return;
    }
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (sendBlockedMissingPhone || sendComposeBlocked) return;
      handleSendMessage();
    }
  };

  const previewText = (text) => {
    if (!text) return "";
    const s = String(text).trim().replace(/\s+/g, " ");
    if (s.length <= SUGGESTION_PREVIEW_LENGTH) return s;
    return `${s.slice(0, SUGGESTION_PREVIEW_LENGTH)}…`;
  };

  /** Encontra trechos em `text` que correspondem a `search` ignorando acentos e retorna partes para destacar em negrito. */
  const highlightMatch = (text, search) => {
    if (!text || !search || !String(search).trim()) return text;
    const str = String(text);
    const term = String(search).trim();
    if (!term) return str;
    const normSearch = normalizeForSearch(term);
    if (!normSearch) return str;
    const chars = Array.from(str);
    let norm = "";
    const normToOrig = [];
    for (let i = 0; i < chars.length; i++) {
      const n = normalizeForSearch(chars[i]);
      if (n.length > 0) {
        for (let j = 0; j < n.length; j++) {
          normToOrig.push({ start: i, end: i + 1 });
          norm += n[j];
        }
      }
    }
    const spans = [];
    let idx = 0;
    while (idx < norm.length) {
      const found = norm.indexOf(normSearch, idx);
      if (found === -1) break;
      const end = found + normSearch.length;
      const oStart = normToOrig[found].start;
      const oEnd = normToOrig[end - 1].end;
      spans.push({ start: oStart, end: oEnd });
      idx = end;
    }
    if (spans.length === 0) return str;
    const parts = [];
    let last = 0;
    spans.forEach(({ start, end }) => {
      if (start > last) {
        parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last, start).join("") });
      }
      parts.push({ key: `m-${start}`, type: "mark", value: chars.slice(start, end).join("") });
      last = end;
    });
    if (last < chars.length) {
      parts.push({ key: `t-${last}`, type: "text", value: chars.slice(last).join("") });
    }
    return parts.map((p) =>
      p.type === "mark" ? (
        <Box
          component="span"
          key={p.key}
          sx={{
            backgroundColor: "grey.200",
            color: "text.primary",
            borderRadius: 0.5,
            px: 0.25,
            fontWeight: 700,
          }}
        >
          {p.value}
        </Box>
      ) : (
        <span key={p.key}>{p.value}</span>
      )
    );
  };

  return (
    <ClickAwayListener onClickAway={closeSuggestions}>
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          gap: isMobile ? 0 : 1,
          paddingBottom: 0,
        }}
      >
        {requiresTemplate && !windowAlertDismissed && (
          <Alert
            severity="info"
            icon={<InfoOutlined sx={{ fontSize: 22 }} />}
            onClose={() => setWindowAlertDismissed(true)}
            sx={{
              // Overlay acima do input para não alterar a altura do composer
              position: "absolute",
              left: 0,
              right: 0,
              bottom: "100%",
              mb: 1,
              py: 1.5,
              px: 2,
              borderRadius: 2,
              border: "1px solid",
              borderColor: "info.light",
              zIndex: 1500,
              "& .MuiAlert-message": { width: "100%", padding: 0 },
            }}
          >
            <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1, color: "info.dark" }}>
              {hasHistory
                ? t("input.windowClosedTitle", "Janela de 24 horas do WhatsApp fechada")
                : t("input.firstContactTitle", "Primeiro contato pelo WhatsApp")}
            </Typography>
            <Typography variant="body2" sx={{ color: "text.secondary", lineHeight: 1.55 }}>
              {hasHistory
                ? hasTemplatesAvailable
                  ? t(
                      "input.windowClosedHasTemplates",
                      "Após 24h da última mensagem do cliente, o WhatsApp só permite enviar templates aprovados. Digite abaixo para ver os disponíveis ou aguarde o cliente responder."
                    )
                  : noUsableReason === "pending"
                    ? t(
                        "input.windowClosedPending",
                        "Após 24h, só templates aprovados pela Meta podem ser enviados. Os deste canal ainda estão em aprovação — quando a Meta aprovar, eles aparecem aqui."
                      )
                    : noUsableReason === "blocked"
                      ? t(
                          "input.windowClosedBlocked",
                          "Após 24h, só templates aprovados podem ser enviados. Abra o canal em Configurações, veja o motivo da recusa e crie um novo template."
                        )
                      : t(
                          "input.windowClosedNoTemplates",
                          "Após 24h, só templates aprovados podem ser enviados. Crie um em Configurações > Canais e aguarde a Meta aprovar."
                        )
                : hasTemplatesAvailable
                  ? t(
                      "input.firstContactHasTemplates",
                      "A primeira mensagem precisa ser um template aprovado. Digite abaixo para buscar um que apresente sua empresa e o motivo do contato."
                    )
                  : noUsableReason === "pending"
                    ? t(
                        "input.firstContactPending",
                        "A primeira mensagem precisa de um template aprovado. Os deste canal ainda estão em aprovação na Meta."
                      )
                    : noUsableReason === "blocked"
                      ? t(
                          "input.firstContactBlocked",
                          "A primeira mensagem precisa de um template aprovado. Abra o canal em Configurações, veja o motivo da recusa e crie um novo."
                        )
                      : t(
                          "input.firstContactNoTemplates",
                          "A primeira mensagem precisa de um template aprovado. Crie um em Configurações > Canais e aguarde a Meta aprovar."
                        )}
            </Typography>
          </Alert>
        )}
        <Box
          sx={
            isMobile
              ? composerMobileInputRowSx
              : {
                  display: "flex",
                  alignItems: "center",
                  gap: 0,
                  width: "100%",
                }
          }
        >
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              display: "flex",
              alignItems: "stretch",
              borderRadius: `${inputBorderRadius}px`,
              minHeight: composerRowHeight,
              // Importante: a lista de templates abre "para cima" (position:absolute; bottom:100%).
              // Se o container tiver overflow:hidden, a dropdown fica clipada (aparece só a sombra).
              overflow: "visible",
              ...composerSurface.shell,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                alignSelf: "stretch",
                px: 0.25,
                ...composerSurface.divider,
              }}
            >
              <ChannelSelector
                integrated
                selectedChannel={selectedChannel}
                setSelectedChannel={setSelectedChannel}
                personalWhatsappComposerOnly={isPersonalWhatsappThread}
                clientConversation={clientConversation}
                conversationMessages={conversationMessages}
              />
            </Box>
            <Box
              ref={anchorRef}
              sx={{
                flex: 1,
                minWidth: 0,
                minHeight: `${composerRowHeight}px`,
                position: "relative",
                display: "flex",
                flexDirection: "column",
              }}
            >
            {suggestionsOpen && !isRecording && !audioURL && (
              <Paper
                elevation={8}
                sx={{
                  position: "absolute",
                  left: 0,
                  right: 0,
                  bottom: "100%",
                  mb: 0.5,
                  maxHeight: SUGGESTIONS_MAX_HEIGHT,
                  overflow: "hidden",
                  // garantir acima de overlays/portals de menções/produtos
                  zIndex: 1400,
                  borderRadius: 2,
                }}
              >
                <Box sx={{ display: "flex", flexDirection: "column", maxHeight: SUGGESTIONS_MAX_HEIGHT }}>
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "flex-end",
                      alignItems: "center",
                      py: 1,
                      pr: 1,
                      pl: 2,
                      flexShrink: 0,
                    }}
                  >
                    <IconButton
                      size="small"
                      onClick={closeSuggestions}
                      sx={{
                        color: "text.secondary",
                        "&:hover": { color: "text.primary", backgroundColor: "action.hover" },
                      }}
                      aria-label="Fechar sugestões"
                    >
                      <Close fontSize="small" />
                    </IconButton>
                  </Box>
                  <List dense disablePadding sx={{ overflow: "auto", flex: 1, minHeight: 0 }}>
                    {(searchTrim.length === 0 && shouldShowTemplatesWhileEmpty ? templatesOnlyList : suggestionsList).map(
                      (item, idx) => (
                      <ListItemButton
                        key={`${item.type}-${item.id}-${idx}`}
                        ref={idx === highlightedIndex ? highlightedItemRef : null}
                        selected={idx === highlightedIndex}
                        onMouseEnter={() => setHighlightedIndex(idx)}
                        onClick={() => selectSuggestion(item)}
                        sx={{
                          whiteSpace: "normal",
                          wordBreak: "break-word",
                          py: 1,
                          borderBottom:
                            idx <
                            (searchTrim.length === 0 && shouldShowTemplatesWhileEmpty
                              ? templatesOnlyList.length
                              : suggestionsList.length) -
                              1
                              ? "1px solid"
                              : "none",
                          borderColor: "divider",
                        }}
                      >
                        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, width: "100%" }}>
                          <Chip
                            size="small"
                            label={item.type === "template" ? "Template" : "Mensagem rápida"}
                            variant="outlined"
                            sx={{
                              flexShrink: 0,
                              height: 20,
                              fontSize: "0.7rem",
                              ...(item.type === "template"
                                ? { borderColor: "primary.main", color: "primary.main" }
                                : { borderColor: "secondary.main", color: "secondary.main" }),
                            }}
                          />
                          <Typography variant="body2" component="span" color="text.primary" sx={{ flex: 1 }}>
                            {highlightMatch(previewText(item.displayText), searchTrim)}
                          </Typography>
                        </Box>
                      </ListItemButton>
                    ))}
                  </List>
                </Box>
              </Paper>
            )}
            {isRecording ? (
              <RecordingIndicator
                embedded
                durationSeconds={recordingDuration}
                onStop={stopRecording}
                onDiscard={discardRecording}
              />
            ) : audioURL ? (
              <AudioPreview embedded audioURL={audioURL} onRemove={onRemoveAudio} />
            ) : (
            <TextField
              fullWidth
              multiline
              minRows={1}
              maxRows={4}
              variant="outlined"
              placeholder={composerPlaceholder}
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              onKeyDown={handleInputKeyDown}
              inputProps={{ ...composeInputHandlers, title: composerPlaceholderTitle }}
              InputProps={{
                style: {
                  borderRadius: 0,
                  fontSize: isMobile ? "16px" : "1rem",
                },
                inputRef: inputRef,
                startAdornment: isMobile ? undefined : (
                  <InputAdornment position="start" sx={{ ml: 0.5 }}>
                    <IconButton
                      onClick={handleEmojiPickerOpen}
                      edge="start"
                      size="medium"
                      aria-label={t("input.emojiAria")}
                    >
                      <InsertEmoticon fontSize="medium" />
                    </IconButton>
                  </InputAdornment>
                ),
                endAdornment: isMobile ? undefined : (
                  <InputAdornment
                    position="end"
                    sx={{ mr: 0.25, display: "flex", alignItems: "center", gap: 0 }}
                  >
                    {showAiButton ? (
                      <Tooltip
                        title={
                          aiDisabled
                            ? isGeneratingSuggestion
                              ? t("input.aiGenerating")
                              : t("input.aiNoHistory")
                            : t("input.aiSuggest")
                        }
                      >
                        <span>
                          <IconButton
                            onClick={onGenerateSuggestion}
                            disabled={aiDisabled}
                            size="medium"
                            aria-label={t("input.aiSuggest")}
                          >
                            <AutoAwesome fontSize="medium" />
                          </IconButton>
                        </span>
                      </Tooltip>
                    ) : null}
                    <ComposeAttachMenu
                      openFilePicker={openFilePicker}
                      fileSendDisabled={requiresTemplate || sendBlockedMissingPhone}
                      fileSendDisabledHelp={
                        requiresTemplate
                          ? t("input.attachOutside24h")
                          : sendBlockedMissingPhone
                            ? t("input.phoneRequiredSnackbar")
                            : null
                      }
                      showAttachProducts={typeof onOpenProductPicker === "function"}
                      onOpenProductPicker={handleOpenProductPickerFromMenu}
                      productsSendDisabled={requiresTemplate || sendBlockedMissingPhone}
                      productsSendDisabledHelp={
                        requiresTemplate
                          ? t("input.attachOutside24h")
                          : sendBlockedMissingPhone
                            ? t("input.phoneRequiredSnackbar")
                            : null
                      }
                      showTemplates={requiresTemplate && hasTemplatesAvailable}
                      onOpenTemplates={openTemplatesManually}
                      templatesDisabled={externalPickerOpen}
                      templatesDisabledHelp={t("input.templatesClosePickerHelp")}
                      showLocation={typeof onSendCurrentLocation === "function"}
                      onSendCurrentLocation={onSendCurrentLocation}
                      locationSendDisabled={locationSendDisabled}
                      locationSending={locationSending}
                      locationSendDisabledHelp={locationSendDisabledHelp}
                      showShareContact={typeof onShareContact === "function"}
                      onShareContact={onShareContact}
                      contactSendDisabled={contactSendDisabled}
                      contactSendDisabledHelp={contactSendDisabledHelp}
                      showStartVideo={typeof onStartVideo === "function"}
                      onStartVideo={onStartVideo}
                      videoSending={videoSending}
                      videoSendDisabled={videoSendDisabled}
                      videoSendDisabledHelp={videoSendDisabledHelp}
                      videoMenuLabel={videoMenuLabel}
                      showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
                      onJoinVideo={onJoinVideo}
                      joinVideoSending={joinVideoSending}
                      showGoogleMeet={typeof onStartGoogleMeet === "function"}
                      onStartGoogleMeet={onStartGoogleMeet}
                      googleMeetSending={googleMeetSending}
                      googleMeetDisabled={googleMeetDisabled}
                      googleMeetDisabledHelp={googleMeetDisabledHelp}
                      showPoll={showPoll}
                      onStartPoll={onStartPoll}
                      pollDisabled={pollDisabled}
                      pollDisabledHelp={pollDisabledHelp}
                      size="medium"
                    />
                  </InputAdornment>
                ),
              }}
              sx={isMobile ? COMPOSER_MOBILE_TEXT_FIELD_SX : COMPOSER_TEXT_FIELD_SX}
            />
            )}
            </Box>
          </Box>
          {isMobile && !isRecording && !audioURL ? (
          <ComposerMobileToolbar>
            <IconButton
              onClick={handleEmojiPickerOpen}
              size="small"
              aria-label={t("input.emojiAria")}
            >
              <InsertEmoticon fontSize="small" />
            </IconButton>
            {showAiButton ? (
              <Tooltip
                title={
                  aiDisabled
                    ? isGeneratingSuggestion
                      ? t("input.aiGenerating")
                      : t("input.aiNoHistory")
                    : t("input.aiSuggest")
                }
              >
                <span>
                  <IconButton
                    onClick={onGenerateSuggestion}
                    disabled={aiDisabled}
                    size="small"
                    aria-label={t("input.aiSuggest")}
                  >
                    <AutoAwesome fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            ) : null}
            <ComposeAttachMenu
              openFilePicker={openFilePicker}
              fileSendDisabled={requiresTemplate || sendBlockedMissingPhone}
              fileSendDisabledHelp={
                requiresTemplate
                  ? t("input.attachOutside24h")
                  : sendBlockedMissingPhone
                    ? t("input.phoneRequiredSnackbar")
                    : null
              }
              showAttachProducts={typeof onOpenProductPicker === "function"}
              onOpenProductPicker={handleOpenProductPickerFromMenu}
              productsSendDisabled={requiresTemplate || sendBlockedMissingPhone}
              productsSendDisabledHelp={
                requiresTemplate
                  ? t("input.attachOutside24h")
                  : sendBlockedMissingPhone
                    ? t("input.phoneRequiredSnackbar")
                    : null
              }
              showTemplates={requiresTemplate && hasTemplatesAvailable}
              onOpenTemplates={openTemplatesManually}
              templatesDisabled={externalPickerOpen}
              templatesDisabledHelp={t("input.templatesClosePickerHelp")}
              showLocation={typeof onSendCurrentLocation === "function"}
              onSendCurrentLocation={onSendCurrentLocation}
              locationSendDisabled={locationSendDisabled}
              locationSending={locationSending}
              locationSendDisabledHelp={locationSendDisabledHelp}
              showShareContact={typeof onShareContact === "function"}
              onShareContact={onShareContact}
              contactSendDisabled={contactSendDisabled}
              contactSendDisabledHelp={contactSendDisabledHelp}
              showStartVideo={typeof onStartVideo === "function"}
              onStartVideo={onStartVideo}
              videoSending={videoSending}
              videoSendDisabled={videoSendDisabled}
              videoSendDisabledHelp={videoSendDisabledHelp}
              videoMenuLabel={videoMenuLabel}
              showJoinVideo={showJoinVideo && typeof onJoinVideo === "function"}
              onJoinVideo={onJoinVideo}
              joinVideoSending={joinVideoSending}
              showGoogleMeet={typeof onStartGoogleMeet === "function"}
              onStartGoogleMeet={onStartGoogleMeet}
              googleMeetSending={googleMeetSending}
              googleMeetDisabled={googleMeetDisabled}
              googleMeetDisabledHelp={googleMeetDisabledHelp}
              showPoll={showPoll}
              onStartPoll={onStartPoll}
              pollDisabled={pollDisabled}
              pollDisabledHelp={pollDisabledHelp}
              size="small"
            />
          </ComposerMobileToolbar>
          ) : null}
          {isMobile ? (
            <IconButton
              onClick={() => {
                if (sendBlockedMissingPhone || sendComposeBlocked) return;
                if (canSend) handleSendMessage();
                else startRecording();
              }}
              disabled={sendBlockedMissingPhone || sendComposeBlocked}
              aria-label={
                canSend ? t("input.sendAria") : t("input.recordVoiceAria")
              }
              sx={{
                ...composerPrimaryActionButtonSx({
                  accent: isPersonalWhatsappThread ? "#128C7E" : undefined,
                  accentHover: isPersonalWhatsappThread ? "#0f6f64" : undefined,
                  size: COMPOSER_MOBILE_ACTION_BUTTON_SIZE_PX,
                }),
                ...(isPersonalWhatsappThread
                  ? { "&:hover": { backgroundColor: "#0f6f64" } }
                  : {}),
              }}
            >
              {canSend ? (
                <Send fontSize="small" />
              ) : (
                <Mic fontSize="small" />
              )}
            </IconButton>
          ) : null}
        </Box>
        {selectionToolbar}
      </Box>
    </ClickAwayListener>
  );
};

export default MessageWhatsappChannel;
